import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import {
  CircularProgress,
  FormControlLabel,
  FormHelperText,
  Grid,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import axios from "axios";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AppLoader from "../../../components/apploader";
import GlobalButton from "../../../components/button";
import AuthContainer from "../../../components/container/AuthContainer";
import ImageUploader from "../../../components/imageUploader";
import InputField from "../../../components/InputField";
import Label from "../../../components/label";
import RomanianPhoneInput from "../../../components/romaniaPhoneInput";
import { useSnackbar } from "../../../lib/context/snackbarContext";
import { baseUrl } from "../../../services";
import { checkCui, sendCodeUser } from "../../../services/authenticationFn";
import { regiterUserValidation } from "../../../utils/validation";
import { t } from "i18next";

const SignupPage = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { showSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(false);
  const [isCuiLoading, setIsCuiLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(true);
  const [signupAs, setSignupAs] = useState("individual");
  const [inputValues, setInputValues] = useState({
    image: null,
    fname: "",
    lname: "",
    email: "",
    contact: "",
    password: "",
    cui: "",
  });
  const [inputError, setInputError] = useState({
    image: false,
    fname: false,
    lname: false,
    email: false,
    isValidEmail: false,
    contact: false,
    password: false,
    isStrongPassword: false,
    cui: false,
    isValidCui: false,
    isCuiExists: false,
  });

  useEffect(() => {
    if (state?.inputValues) {
      setSignupAs(state.inputValues.signupAs);
      setInputValues((val) => ({
        ...state.inputValues,
      }));
    }
  }, [state?.inputValues]);

  const handleChangeInputs = (event) => {
    const { name, value } = event.target;

    if ((name === "fname" || name === "lname") && value.length > 15) return;

    setInputError((err) => ({
      ...err,
      [name]: false,
      isValidEmail: name === "email" ? false : inputError.email,
      isStrongPassword:
        name === "password" ? false : inputError.isStrongPassword,
      isValidCui: name === "cui" ? false : inputError.isValidCui,
      isCuiExists: name === "cui" ? false : inputError.isCuiExists,
    }));
    setInputValues((val) => ({ ...val, [name]: value }));
  };

  const handleImageChange = async (event) => {
    const file = event.target.files[0];

    const maxSize = 5 * 1024 * 1024;

    if (file.size > maxSize) {
      return showSnackbar(t("imageSizeLimit"), "error");
    }

    const imageType = file.type.split("/")[1].toLowerCase();

    const allowedFormats = ["png", "jpeg", "jpg"];

    if (!allowedFormats.includes(imageType)) {
      return showSnackbar(t("imageFormatRestriction"), "error");
    }

    try {
      setIsLoading(true);
      const formData = new FormData();
      formData.append("image", event.target.files[0]);
      const response = await axios({
        method: "post",
        // url: "https://sirldigital.com/uploadService.php",
        url: `${baseUrl}/image/upload`,
        data: formData,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setInputValues((prevValues) => ({
        ...prevValues,
        image: response?.data?.url,
      }));
      setInputError((prevError) => ({
        ...prevError,
        image: false,
      }));
      showSnackbar(t("imageUploadSuccess"), "success");
    } catch (error) {
      showSnackbar(t("imageUploadFailed"), "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendCodeForRegister = async () => {
    const validate = regiterUserValidation(
      inputValues,
      setInputError,
      signupAs
    );
    if (validate) return;

    let allowed = false;

    if (signupAs === "business" && !inputError.isCuiExists) {
      try {
        setIsCuiLoading(true);
        const response = await checkCui({ cui: inputValues.cui });
        if (response.data.status === "fail") {
          allowed = true;
        }
      } catch (error) {
        showSnackbar(error?.message, "error");
      } finally {
        setIsCuiLoading(false);
      }
    }

    if (allowed) {
      return setInputError((prev) => ({ ...prev, isCuiExists: true }));
    }

    const payload = {
      email: inputValues.email,
      purpose: "Registration",
      role: "User",
    };

    try {
      setIsLoading(true);
      const response = await sendCodeUser(payload);
      if (response.data.status === "success") {
        navigate("/register/verification", {
          state: { inputValues: { ...inputValues, signupAs } },
        });
        showSnackbar(response?.data?.message, "success");
      } else {
        showSnackbar(response?.data?.message, "error");
      }
    } catch (error) {
      showSnackbar(error?.message, "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthContainer>
      <AppLoader isVisible={isLoading} />
      <Stack
        position={"absolute"}
        top={20}
        left={30}
        direction={"row"}
        alignItems={"center"}
        gap={1}
      >
        <Typography variant="body1" color={"#8692A6"}>
          {t("alreadyHaveAcc")} ?
        </Typography>
        <Typography
          variant="body1"
          fontWeight={600}
          color={"primary.main"}
          sx={{ cursor: "pointer" }}
          onClick={() => navigate("/login")}
          textTransform={"capitalize"}
        >
          {t("logIn")}
        </Typography>
      </Stack>
      <Stack textAlign={"center"} mt={6}>
        <Typography variant="heading_pr">{t("registration")}</Typography>
        <Typography variant="heading_sec">{t("registrationMsg")}</Typography>
      </Stack>

      <Stack alignItems={"center"} mt={3}>
        <ImageUploader
          error={inputError.image}
          image={inputValues.image}
          handleImageChange={handleImageChange}
        />
        {inputError.image && (
          <FormHelperText sx={{ color: "custom.errorRed" }}>
            {t("imageRequired")}
          </FormHelperText>
        )}
      </Stack>

      <Grid container spacing={1} mt={3}>
        <Grid item xs={12}>
          <Stack spacing={2}>
            <Typography variant="body1" fontWeight={500} color="text.primary">
              {t("signupAs")}
            </Typography>
            <RadioGroup
              row
              value={signupAs}
              onChange={(e) => {
                setSignupAs(e.target.value);
                setInputError((prev) => ({
                  ...prev,
                  fname: false,
                  lname: false,
                  email: false,
                  isValidEmail: false,
                  contact: false,
                  password: false,
                  isStrongPassword: false,
                  cui: false,
                  isValidCui: false,
                  isCuiExists: false,
                }));
              }}
              sx={{
                "& .MuiFormControlLabel-root": {
                  marginRight: 3,
                },
              }}
            >
              <FormControlLabel
                value="individual"
                control={
                  <Radio
                    sx={{
                      color: "primary.main",
                      "&.Mui-checked": {
                        color: "primary.main",
                      },
                    }}
                  />
                }
                label={
                  <Typography
                    variant="body2"
                    color={
                      signupAs === "individual"
                        ? "primary.main"
                        : "text.secondary"
                    }
                    fontWeight={signupAs === "individual" ? 600 : 400}
                  >
                    {t("individual")}
                  </Typography>
                }
              />
              <FormControlLabel
                value="business"
                control={
                  <Radio
                    sx={{
                      color: "primary.main",
                      "&.Mui-checked": {
                        color: "primary.main",
                      },
                    }}
                  />
                }
                label={
                  <Typography
                    variant="body2"
                    color={
                      signupAs === "business"
                        ? "primary.main"
                        : "text.secondary"
                    }
                    fontWeight={signupAs === "business" ? 600 : 400}
                  >
                    {t("business")}
                  </Typography>
                }
              />
            </RadioGroup>
          </Stack>
        </Grid>
        <Grid item xs={signupAs === "individual" ? 6 : 12}>
          <Label
            text={
              signupAs === "individual" ? t("firstName") : t("businessName")
            }
            required
            htmlFor="user-fname"
          />
          <InputField
            id={"user-fname"}
            required
            inputError={inputError.fname}
            helperText={
              inputError.fname &&
              (signupAs === "individual"
                ? t("firstNameRequire")
                : t("businessNameRequired"))
            }
            placeholder={`${t("enter")} ${
              signupAs === "individual" ? t("firstName") : t("businessName")
            }`}
            type="text"
            name={"fname"}
            value={inputValues.fname}
            onChange={handleChangeInputs}
          />
        </Grid>

        {signupAs === "individual" && (
          <Grid item xs={6}>
            <Label text={t("lastName")} required htmlFor="user-lname" />
            <InputField
              id={"user-lname"}
              required
              inputError={inputError.lname}
              helperText={inputError.lname && t("lastNameRequire")}
              placeholder={`${t("enter")} ${t("lastName")}`}
              type="text"
              name={"lname"}
              value={inputValues.lname}
              onChange={handleChangeInputs}
            />
          </Grid>
        )}
        {signupAs === "business" && (
          <Grid item xs={12}>
            <Label text={`${t("cui")} (${t("startsWithRO")})`} required htmlFor="user-cui" />
            <InputField
              id={"user-cui"}
              required
              inputError={
                inputError.cui || inputError.isValidCui || inputError.isCuiExists
              }
              helperText={
                inputError.cui
                  ? t("cuiRequired")
                  : inputError.isValidCui
                  ? t("invalidCuiFormat")
                  : inputError.isCuiExists
                  ? t("cuiAlreadyExists")
                  : null
              }
              placeholder={`${t("enter")} ${t("cui")}`}
              type="text"
              name={"cui"}
              value={inputValues.cui}
              onChange={handleChangeInputs}
              InputEndIcon={
                isCuiLoading ? <CircularProgress size={20} /> : null
              }
            />
          </Grid>
        )}
        <Grid item xs={12}>
          <Label text={t("emailAddress")} required htmlFor="user-email" />
          <InputField
            id={"user-email"}
            required
            inputError={inputError.email || inputError.isValidEmail}
            helperText={
              inputError.email
                ? t("emailAddRequired")
                : inputError.isValidEmail
                ? t("invalidEmailFormat")
                : null
            }
            placeholder={`${t("enter")} ${t("emailAddress")}`}
            type="email"
            name={"email"}
            value={inputValues.email}
            onChange={handleChangeInputs}
          />
        </Grid>
        <Grid item xs={12}>
          <Label text={"Contact"} required htmlFor="user-contact" />
          <RomanianPhoneInput
            value={inputValues.contact}
            onChange={(value) => {
              setInputValues({ ...inputValues, contact: value });
              setInputError({ ...inputError, contact: false });
            }}
            inputError={inputError.contact}
          />
          {inputError.contact && (
            <FormHelperText sx={{ color: "#d32f2f" }}>
              {t("contactRequired")}
            </FormHelperText>
          )}
          {/* <InputField
            id={"user-contact"}
            required
            inputError={inputError.contact}
            helperText={inputError.contact && "Contact is required"}
            placeholder={"Enter contact number"}
            type="number"
            name={"contact"}
            value={inputValues.contact}
            onChange={handleChangeInputs}
          /> */}
        </Grid>
        <Grid item xs={12}>
          <Label text={t("password")} required htmlFor="user_reg_pass" />
          <InputField
            id={"user_reg_pass"}
            required
            placeholder={`${t("enter")} ${t("newPassword")}`}
            type={showPassword ? "password" : "text"}
            InputEndIcon={
              showPassword ? (
                <VisibilityOffIcon
                  sx={{
                    cursor: "pointer",
                  }}
                  onClick={() => setShowPassword(false)}
                />
              ) : (
                <VisibilityIcon
                  sx={{
                    cursor: "pointer",
                  }}
                  onClick={() => setShowPassword(true)}
                />
              )
            }
            inputError={inputError.password || inputError.isStrongPassword}
            helperText={
              inputError.password
                ? t("passwordRequired")
                : inputError.isStrongPassword
                ? t("passwordValidationDesc")
                : null
            }
            name={"password"}
            value={inputValues.password}
            onChange={handleChangeInputs}
          />
        </Grid>
      </Grid>

      <GlobalButton
        btnText={"letsCreateYourAccount"}
        handleClickBtn={handleSendCodeForRegister}
        disabled={inputError.isCuiExists || isCuiLoading}
        styleProps={{
          width: "100%",
          height: 50,
          mt: 4,
          fontSize: "1.125rem",
        }}
      />
      {/* <SocialLogins /> */}
    </AuthContainer>
  );
};

export default SignupPage;
